/**
 * Client Feature Flags Provider
 *
 * Provides client-side feature flags with AsyncStorage persistence.
 * These flags are different from server-side feature flags (FeatureFlags.tsx)
 * which are fetched from the GraphQL API.
 *
 * Currently supports:
 * - reactProfilerEnabled: Controls whether React Profiler is enabled (default: true)
 * - expandableFABEnabled: Controls whether expandable FAB with menu is enabled (default: false)
 * - leftSidebarEnabled: Controls whether the left sidebar navigation is enabled (default: true)
 * - playerPageHeaderEnabled: Controls whether the new PlayerPageHeader component is shown (default: false)
 *
 * Features:
 * - Persists flag values to AsyncStorage for persistence across app restarts
 * - Loads flag values from AsyncStorage on mount
 * - Provides toggle and set methods for each flag
 * - Handles AsyncStorage errors gracefully with fallback to default values
 * - Uses namespaced AsyncStorage key: @gemini-sports-ai:client-feature-flags
 * @module providers/ClientFeatureFlagsProvider
 * @see {@link https://react-native-async-storage.github.io/async-storage/ | AsyncStorage Documentation}
 */

import AsyncStorage from "@react-native-async-storage/async-storage";
import React, {
  createContext,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from "react";

/**
 * Storage key for client feature flags in AsyncStorage
 */
const STORAGE_KEY = "@gemini-sports-ai:client-feature-flags";

/**
 * Interface for client feature flags state
 */
interface ClientFeatureFlags {
  reactProfilerEnabled: boolean;
  expandableFABEnabled: boolean;
  leftSidebarEnabled: boolean;
  playerPageHeaderEnabled: boolean;
}

/**
 * Default values for client feature flags
 */
const DEFAULT_FLAGS: ClientFeatureFlags = {
  reactProfilerEnabled: true,
  expandableFABEnabled: false,
  leftSidebarEnabled: true,
  playerPageHeaderEnabled: true,
};

/**
 * Context value interface for ClientFeatureFlagsProvider
 */
interface ClientFeatureFlagsContextValue extends ClientFeatureFlags {
  /**
   * Toggles the React Profiler enabled state
   */
  toggleReactProfiler: () => void;

  /**
   * Sets the React Profiler enabled state
   * @param enabled - Whether React Profiler should be enabled
   */
  setReactProfilerEnabled: (enabled: boolean) => void;

  /**
   * Toggles the expandable FAB enabled state
   */
  toggleExpandableFAB: () => void;

  /**
   * Sets the expandable FAB enabled state
   * @param enabled - Whether expandable FAB should be enabled
   */
  setExpandableFABEnabled: (enabled: boolean) => void;

  /**
   * Toggles the left sidebar enabled state
   */
  toggleLeftSidebar: () => void;

  /**
   * Sets the left sidebar enabled state
   * @param enabled - Whether left sidebar should be enabled
   */
  setLeftSidebarEnabled: (enabled: boolean) => void;

  /**
   * Toggles the player page header enabled state
   */
  togglePlayerPageHeader: () => void;

  /**
   * Sets the player page header enabled state
   * @param enabled - Whether player page header should be enabled
   */
  setPlayerPageHeaderEnabled: (enabled: boolean) => void;
}

/**
 * Context for client feature flags
 * Exported for use by the useClientFeatureFlags hook
 */
export const ClientFeatureFlagsContext =
  createContext<ClientFeatureFlagsContextValue>({
    ...DEFAULT_FLAGS,
    toggleReactProfiler: () => {},
    setReactProfilerEnabled: () => {},
    toggleExpandableFAB: () => {},
    setExpandableFABEnabled: () => {},
    toggleLeftSidebar: () => {},
    setLeftSidebarEnabled: () => {},
    togglePlayerPageHeader: () => {},
    setPlayerPageHeaderEnabled: () => {},
  });

/**
 * Props for ClientFeatureFlagsProvider
 */
interface ClientFeatureFlagsProviderProps {
  children: ReactNode;
}

/**
 * Provider component for client feature flags
 *
 * Manages client-side feature flags with AsyncStorage persistence.
 * Loads flags from storage on mount and saves changes automatically.
 * @param props - Component props
 * @param props.children - Child components to wrap
 * @returns Provider component wrapping children
 * @example
 * ```tsx
 * <ClientFeatureFlagsProvider>
 *   <App />
 * </ClientFeatureFlagsProvider>
 * ```
 */
export /**
 *
 */
const ClientFeatureFlagsProvider = ({
  children,
}: ClientFeatureFlagsProviderProps) => {
  const [flags, setFlags] = useState<ClientFeatureFlags>(DEFAULT_FLAGS);

  /**
   * Saves feature flags to AsyncStorage
   */
  const saveFlags = useCallback(async (newFlags: ClientFeatureFlags) => {
    try {
      await AsyncStorage.setItem(STORAGE_KEY, JSON.stringify(newFlags));
    } catch (error) {
      console.error(
        "Failed to save client feature flags to AsyncStorage:",
        error
      );
    }
  }, []);

  /**
   * Toggles the React Profiler enabled state
   */
  const toggleReactProfiler = useCallback(() => {
    setFlags(prevFlags => {
      const newFlags = {
        ...prevFlags,
        reactProfilerEnabled: !prevFlags.reactProfilerEnabled,
      };
      saveFlags(newFlags);
      return newFlags;
    });
  }, [saveFlags]);

  /**
   * Sets the React Profiler enabled state
   */
  const setReactProfilerEnabled = useCallback(
    (enabled: boolean) => {
      setFlags(prevFlags => {
        const newFlags = {
          ...prevFlags,
          reactProfilerEnabled: enabled,
        };
        saveFlags(newFlags);
        return newFlags;
      });
    },
    [saveFlags]
  );

  /**
   * Toggles the expandable FAB enabled state
   */
  const toggleExpandableFAB = useCallback(() => {
    setFlags(prevFlags => {
      const newFlags = {
        ...prevFlags,
        expandableFABEnabled: !prevFlags.expandableFABEnabled,
      };
      saveFlags(newFlags);
      return newFlags;
    });
  }, [saveFlags]);

  /**
   * Sets the expandable FAB enabled state
   */
  const setExpandableFABEnabled = useCallback(
    (enabled: boolean) => {
      setFlags(prevFlags => {
        const newFlags = {
          ...prevFlags,
          expandableFABEnabled: enabled,
        };
        saveFlags(newFlags);
        return newFlags;
      });
    },
    [saveFlags]
  );

  /**
   * Toggles the left sidebar enabled state
   */
  const toggleLeftSidebar = useCallback(() => {
    setFlags(prevFlags => {
      const newFlags = {
        ...prevFlags,
        leftSidebarEnabled: !prevFlags.leftSidebarEnabled,
      };
      saveFlags(newFlags);
      return newFlags;
    });
  }, [saveFlags]);

  /**
   * Sets the left sidebar enabled state
   */
  const setLeftSidebarEnabled = useCallback(
    (enabled: boolean) => {
      setFlags(prevFlags => {
        const newFlags = {
          ...prevFlags,
          leftSidebarEnabled: enabled,
        };
        saveFlags(newFlags);
        return newFlags;
      });
    },
    [saveFlags]
  );

  /**
   * Toggles the player page header enabled state
   */
  const togglePlayerPageHeader = useCallback(() => {
    setFlags(prevFlags => {
      const newFlags = {
        ...prevFlags,
        playerPageHeaderEnabled: !prevFlags.playerPageHeaderEnabled,
      };
      saveFlags(newFlags);
      return newFlags;
    });
  }, [saveFlags]);

  /**
   * Sets the player page header enabled state
   */
  const setPlayerPageHeaderEnabled = useCallback(
    (enabled: boolean) => {
      setFlags(prevFlags => {
        const newFlags = {
          ...prevFlags,
          playerPageHeaderEnabled: enabled,
        };
        saveFlags(newFlags);
        return newFlags;
      });
    },
    [saveFlags]
  );

  const contextValue: ClientFeatureFlagsContextValue = useMemo(
    () => ({
      reactProfilerEnabled: flags.reactProfilerEnabled,
      expandableFABEnabled: flags.expandableFABEnabled,
      leftSidebarEnabled: flags.leftSidebarEnabled,
      playerPageHeaderEnabled: flags.playerPageHeaderEnabled,
      toggleReactProfiler,
      setReactProfilerEnabled,
      toggleExpandableFAB,
      setExpandableFABEnabled,
      toggleLeftSidebar,
      setLeftSidebarEnabled,
      togglePlayerPageHeader,
      setPlayerPageHeaderEnabled,
    }),
    [
      flags.reactProfilerEnabled,
      flags.expandableFABEnabled,
      flags.leftSidebarEnabled,
      flags.playerPageHeaderEnabled,
      toggleReactProfiler,
      setReactProfilerEnabled,
      toggleExpandableFAB,
      setExpandableFABEnabled,
      toggleLeftSidebar,
      setLeftSidebarEnabled,
      togglePlayerPageHeader,
      setPlayerPageHeaderEnabled,
    ]
  );

  /**
   * Loads feature flags from AsyncStorage on mount
   */
  useEffect(() => {
    const loadFlags = async () => {
      try {
        const storedValue = await AsyncStorage.getItem(STORAGE_KEY);
        if (storedValue) {
          const parsedFlags = JSON.parse(storedValue) as ClientFeatureFlags;
          setFlags(parsedFlags);
        }
      } catch (error) {
        console.error(
          "Failed to load client feature flags from AsyncStorage:",
          error
        );
        // Fall back to default values on error
        setFlags(DEFAULT_FLAGS);
      }
    };

    loadFlags();
  }, []);

  return (
    <ClientFeatureFlagsContext.Provider value={contextValue}>
      {children}
    </ClientFeatureFlagsContext.Provider>
  );
};
